if (!customElements.get('custom-swatches')) {
  // A/B dual-arm (spec 2026-08-18): grid rows/pickers marked combined-only count
  // only for the combined cohort. Pre-stamp (attribute absent) => normal-only —
  // conservative, matches the fallback outcome. Class-based on purpose: the grid
  // is HEIGHT-toggled (display:flex !important in component-product-swatches.css),
  // so geometry (offsetParent) cannot distinguish cohort-hidden rows.
  function comboRowActive(el) {
    if (!el.closest('.combo-control-combined')) return true;
    return document.documentElement.getAttribute('data-combo-arm') === 'combined';
  }

  // Late-stamp reveal sync (3s fallback / slow IG). Module-scope + live query —
  // custom-swatches is replaced wholesale on size changes and swaps, so a
  // per-instance document listener would capture a dead `this` and throw.
  // { once: true } is safe: decide() dispatches at most once per pageview.
  document.addEventListener(
    'combo:arm-revealed',
    () => {
      const grid = document.querySelector('custom-swatches');
      if (grid && typeof grid.syncArmState === 'function') grid.syncArmState();
    },
    { once: true },
  );

  // Bundle color clicks reload the product gallery to preview the color, and
  // every engine reacts differently to that swap: Safari re-snaps/shifts when
  // the snap-scrolling gallery is replaced (often only after its images load),
  // Firefox re-anchors on the removed node, Chrome usually compensates.
  // Neutralize the cause AND the symptom: hold the gallery's height steady
  // until the new images settle, and revert any viewport movement in the
  // settle window that was not initiated by real user input.
  // The buy box lives in a position:sticky column. When heights change during
  // a bundle interaction (gallery swap, the Colors: list wrapping, the qty
  // stepper appearing), Safari — which has no scroll anchoring — recomputes the
  // sticky position and the column visually slides WITHOUT any scroll event
  // (window.scrollY never changes, so scroll-based pins can't see it). Anchor
  // the viewport to the element the user is interacting with instead: each
  // frame, if the anchor drifted on screen without user input, scroll by the
  // drift to keep it stationary. Chrome/Firefox native anchoring already holds
  // the anchor still there, so the correction is a no-op outside Safari.
  function holdViewportAnchor(anchorEl, durationMs) {
    if (!anchorEl || !anchorEl.getBoundingClientRect) return;
    let userMoved = false;
    const markUser = () => {
      userMoved = true;
    };
    const opts = { passive: true, capture: true };
    const inputEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    inputEvents.forEach((t) => window.addEventListener(t, markUser, opts));
    let anchorTop = anchorEl.getBoundingClientRect().top;
    const startedAt = performance.now();
    const stop = () => inputEvents.forEach((t) => window.removeEventListener(t, markUser, opts));
    function tick() {
      if (userMoved || performance.now() - startedAt > durationMs) {
        stop();
        return;
      }
      if (document.contains(anchorEl)) {
        const top = anchorEl.getBoundingClientRect().top;
        const delta = top - anchorTop;
        if (Math.abs(delta) > 1) window.scrollBy(0, delta);
        anchorTop = anchorEl.getBoundingClientRect().top;
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function replaceGalleryPreservingScroll(oldGallery, newGallery, afterReplace) {
    const x = window.scrollX;
    const y = window.scrollY;
    const oldHeight = oldGallery.getBoundingClientRect().height;
    if (oldHeight > 0) newGallery.style.minHeight = oldHeight + 'px';
    oldGallery.replaceWith(newGallery);
    if (afterReplace) afterReplace();
    window.scrollTo(x, y);
    if (oldHeight > 0) {
      // release the height lock once the new images have loaded (or after a
      // grace period), when the column can settle without moving the viewport
      const pending = [...newGallery.querySelectorAll('img')]
        .filter((img) => !img.complete)
        .map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            }),
        );
      Promise.race([Promise.all(pending), new Promise((resolve) => setTimeout(resolve, 2500))]).then(() => {
        newGallery.style.minHeight = '';
      });
    }
  }

  class CustomSwatches extends HTMLElement {
    constructor() {
      super();
      this.isInitializing = false;
    }

    initialize() {
      this.isInitializing = true;
      this.swatches = this.querySelectorAll('.bundle-swatch');
      this.decrementBtns = this.querySelectorAll('.bundle-number-input-decrement');
      this.incrementBtns = this.querySelectorAll('.bundle-number-input-increment');
      this.atcBtn = this.querySelector('.custom-swatches-btn');
      this.inputs = this.querySelectorAll('input');

      this.customSwatchesLabel = this.querySelector('.custom-swatches-label');
      this.container = this.closest('.product-options-form-container');
      this.atcBtn = document.querySelector('.custom-swatches-btn-mobile');
      this.checkbox = document.querySelector('.ProductForm-purchase-options-checkbox');

      this.totalSelected = 0;
      this.colorsSelected = [];

      this.checkboxInput = this.checkbox.querySelector('input');

      // Mix & match minimum quantity (theme setting bundle_min_quantity via data attribute)
      this.minQty = parseInt(this.dataset.minQty, 10) || 3;

      // Store bound function references for proper cleanup
      this.boundDecrement = this.decrement.bind(this);
      this.boundIncrement = this.increment.bind(this);
      this.boundToggleSelected = this.toggleSelected.bind(this);
      this.boundToggleCheckbox = this.toggleCheckbox.bind(this);
      this.boundAddToCart = this.addToCart.bind(this);

      if (this.dataset.fetchRendered === 'true') {
        this.dataset.collapsed = 'false';
        this.style.transition = 'none';

        requestAnimationFrame(() => {
          this.style.height = `${this.scrollHeight}px`;
          this.style.height = 'auto';

          requestAnimationFrame(() => {
            this.style.transition = 'all 300ms ease-out';
          });
        });

        const checkedCheckbox = this.checkbox.querySelector('.custom-checkbox-check');
        if (checkedCheckbox) {
          this.atcBtn.style.display = 'block';
        }
      }

      window.dataLayer = window.dataLayer || [];
      this.decrementBtns.forEach((btn) => {
        btn.addEventListener('click', this.boundDecrement);
      });
      this.incrementBtns.forEach((btn) => {
        btn.addEventListener('click', this.boundIncrement);
      });
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', this.boundToggleSelected);
      });

      this.checkbox.addEventListener('click', this.boundToggleCheckbox);
      if (this.checkbox.classList.contains('background')) {
        this.style.display = 'flex';
      } else {
        this.style.display = 'none';
      }

      this.allOOS = Array.from(this.swatches)
        .filter((swatch) => comboRowActive(swatch))
        .every((swatch) => swatch.classList.contains('bundle-swatch-oos'));

      if (this.allOOS) {
        this.atcBtn.style.display = 'none';
        this.customSwatchesLabel.style.display = 'none';
      }

      this.atcBtn.addEventListener('click', this.boundAddToCart);

      const checkbox = this.checkbox.querySelector('.custom-purchase-option-checkbox');

      if (checkbox.classList.contains('custom-checkbox-check')) {
        if (typeof setSizes !== 'undefined') {
          const sizes = document.querySelectorAll('variant-selects label');
          sizes.forEach((size) => {
            if (setSizes.includes(size.innerText)) {
              const container = size.closest('label');
              if (!container.classList.contains('bundle-size-available')) {
                container.classList.add('bundle-size-available');
              }
            }
          });
        }

        if (!this.allOOS) {
          const bisContainer = document.querySelector('.klaviyo-bis-container');
          if (bisContainer) {
            bisContainer.style.display = 'none';
          }
        } else {
          const bisContainer = document.querySelector('.klaviyo-bis-container');
          if (bisContainer) {
            bisContainer.style.display = 'block';
          }
        }
      }

      // State-vs-event contract (spec §3.2): the stamp usually lands mid-parse,
      // before this element exists — read the state here; the event covers late stamps.
      if (document.documentElement.hasAttribute('data-combo-arm')) {
        this.syncArmState();
      }

      this.isInitializing = false;
    }

    clear() {
      if (this.decrementBtns && this.boundDecrement) {
        this.decrementBtns.forEach((btn) => {
          btn.removeEventListener('click', this.boundDecrement);
        });
      }

      if (this.incrementBtns && this.boundIncrement) {
        this.incrementBtns.forEach((btn) => {
          btn.removeEventListener('click', this.boundIncrement);
        });
      }

      if (this.swatches && this.boundToggleSelected) {
        this.swatches.forEach((swatch) => {
          swatch.removeEventListener('click', this.boundToggleSelected);
        });
      }

      if (this.checkbox && this.boundToggleCheckbox) {
        this.checkbox.removeEventListener('click', this.boundToggleCheckbox);
      }

      if (this.atcBtn && this.boundAddToCart) {
        this.atcBtn.removeEventListener('click', this.boundAddToCart);
      }

      this.totalSelected = 0;
      this.colorsSelected = [];

      if (this.inputs) {
        this.inputs.forEach((input) => {
          input.value = 0;
        });
      }

      const selectedQuantities = this.querySelectorAll('.bundle-selected-quantity');
      selectedQuantities.forEach((qty) => {
        qty.textContent = '';
        qty.classList.remove('has-quantity');
      });

      if (this.swatches) {
        this.swatches.forEach((swatch) => {
          swatch.classList.remove('selected');
        });
      }

      if (this.customSwatchesLabel) {
        this.customSwatchesLabel.innerHTML = 'Color';
      }

      if (this.atcBtn) {
        this.atcBtn.disabled = true;
        this.atcBtn.innerHTML = `<span class="btn-text">Choose ${this.minQty || 3} or more</span>`;
        this.atcBtn.classList.remove('loading');
        this.atcBtn.removeAttribute('aria-disabled');
      }

      this.swatches = null;
      this.decrementBtns = null;
      this.incrementBtns = null;
      this.atcBtn = null;
      this.inputs = null;
      this.customSwatchesLabel = null;
      this.container = null;
      this.checkbox = null;
      this.checkboxInput = null;

      // Clear bound function references
      this.boundDecrement = null;
      this.boundIncrement = null;
      this.boundToggleSelected = null;
      this.boundToggleCheckbox = null;
      this.boundAddToCart = null;

      // Reset the initialization flag
      this.isInitializing = false;
    }

    connectedCallback() {
      if (this.isInitializing) return;

      this.initialize();
    }

    disconnectedCallback() {
      this.clear();
    }

    toggleCheckbox(e) {
      e.preventDefault();

      const checkbox = this.checkbox.querySelector('.custom-purchase-option-checkbox');
      const sizes = document.querySelectorAll('variant-selects label');
      const productFormControls = document.querySelector('.ProductForm-controls');
      // Combo PDPs render one <product-swatches> per style group — toggle them all
      const originalSwatchesList = [...document.querySelectorAll('product-swatches')];

      this.checkbox.classList.toggle('background');
      checkbox.classList.toggle('custom-checkbox-check');

      if (checkbox.classList.contains('custom-checkbox-check')) {
        if (window.innerWidth >= 990) {
          this.atcBtn.style.display = 'block';
        }

        if (typeof setSizes !== 'undefined') {
          sizes.forEach((size) => {
            if (setSizes.includes(size.innerText)) {
              const container = size.closest('label');
              if (!container.classList.contains('bundle-size-available')) {
                container.classList.add('bundle-size-available');
              }
            }
          });
        }

        if (!this.allOOS) {
          const bisContainer = document.querySelector('.klaviyo-bis-container');
          if (bisContainer) {
            bisContainer.style.display = 'none';
          }
        }

        originalSwatchesList.forEach((el) => {
          if (el.offsetParent !== null) this.collapseSection(el);
        });

        const thisInstance = document.querySelector('custom-swatches');
        if (thisInstance) {
          this.expandSection(thisInstance);
        }

        const qtyContainer = document.querySelector('.product-form__quantity');
        const regularAtC = document.querySelector('.buy-buttons-container');
        if (qtyContainer && regularAtC) {
          qtyContainer.style.display = 'none';
          regularAtC.style.display = 'none';
          this.atcBtn.style.display = 'block';
        }
      } else {
        // unchecking restores the current product's gallery — same async swap,
        // same viewport guard as toggleSelected
        holdViewportAnchor(this, 1600);
        fetch(`${window.location.href}&section_id=${this.dataset.sectionId}`)
          .then((response) => response.text())
          .then((data) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data, 'text/html');

            const oldGallery = document.querySelector('.product__media-wrapper');
            const newGallery = doc.querySelector('.product__media-wrapper');

            if (oldGallery && newGallery) {
              replaceGalleryPreservingScroll(oldGallery, newGallery, () => {
                const galleryContainer = document.querySelector('.ProductDetails');

                if (galleryContainer) {
                  loadImageGalleries(galleryContainer);
                }
              });
            }
          });

        document.querySelectorAll('.bundle-size-available').forEach((size) => {
          size.classList.remove('bundle-size-available');
        });

        const bisContainer = document.querySelector('.klaviyo-bis-container');
        if (bisContainer) {
          bisContainer.style.display = 'block';
        }

        originalSwatchesList.forEach((el) => {
          if (el.offsetParent !== null) this.expandSection(el);
        });

        const thisInstance = document.querySelector('custom-swatches');
        if (thisInstance) {
          this.collapseSection(thisInstance);
        }

        const qtyContainer = document.querySelector('.product-form__quantity');
        const regularAtC = document.querySelector('.buy-buttons-container');
        if (qtyContainer && regularAtC) {
          qtyContainer.style.display = 'block';
          regularAtC.style.display = 'block';
          this.atcBtn.style.display = 'none';
        }
      }

      const is_checked = checkbox.classList.contains('custom-checkbox-check');
      //   window.dataLayer.push({
      //     event: 'pdp_checkbox_click',
      //     is_checked: is_checked,
      //     action: 'click',
      //   });
    }

    checkAddToCartConditions() {
      // Recompute from cohort-active inputs: an arm reveal changes which rows count
      // without any click, and restored/programmatic hidden-input values must not gate
      this.totalSelected = Array.from(this.inputs)
        .filter((input) => comboRowActive(input))
        .reduce((sum, input) => sum + (Number(input.value) || 0), 0);
      if (this.totalSelected >= this.minQty) {
        this.atcBtn.disabled = false;
        this.atcBtn.innerHTML = `<span class="btn-text">ADD TO BAG</span>`;
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.innerHTML = `<span class="btn-text">Choose ${this.minQty} or more</span>`;
      }
    }

    capitalizeFirstLetter(val) {
      return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }

    async addToCart() {
      const items = [];
      const validInputs = Array.from(this.inputs).filter(
        (input) => comboRowActive(input) && Number(input.value) > 0,
      );
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      if (!cartDrawer) return;

      this.atcBtn.setAttribute('aria-disabled', true);
      this.atcBtn.classList.add('loading');

      validInputs.forEach((input) => {
        const id = input.getAttribute('id').split('bundle-quantity-')[1];
        const quantity = input.value;

        items.push({
          id: id,
          quantity: Number(quantity),
          properties: { _test_group: 'true' },
        });
      });

      const formData = {
        items: items,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      };

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(formData),
        });

        const responseData = await response.json();

        if (responseData.status) {
          console.error('Error adding to cart:', responseData.description);
          return;
        }

        // Set active element for focus management
        cartDrawer.setActiveElement(document.activeElement);

        // Remove empty state class before rendering contents
        const drawerInner = cartDrawer.querySelector('.drawer__inner');
        if (drawerInner && drawerInner.classList.contains('is-empty')) {
          drawerInner.classList.remove('is-empty');
        }
        cartDrawer.classList.remove('is-empty');

        // Render the cart contents
        cartDrawer.renderContents(responseData);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        this.atcBtn.classList.remove('loading');
        this.atcBtn.removeAttribute('aria-disabled');
      }
    }

    toggleSelected(e) {
      const container = e.target.closest('.bundle-swatch');
      if (!comboRowActive(container)) return;
      // the color click kicks off an async gallery reload — guard the viewport
      // from click until well past the swap + image settle (engine-internal
      // jumps get reverted; real user scrolling is respected)
      holdViewportAnchor(this, 1600);
      const selected = this.querySelector('.bundle-swatch.selected');

      const inputContainer = container.nextElementSibling;
      const input = inputContainer.querySelector('input');
      const max = parseInt(input.getAttribute('max'));

      const selectedQuantity = container.closest('.bundle-swatch-container').querySelector('.bundle-selected-quantity');

      const urlToFetch = window.location.href.split('/products')[0];

      fetch(
        `${urlToFetch}/${container.dataset.fetchLink}?variant=${container.dataset.variantId}&section_id=${this.dataset.sectionId}`,
      )
        .then((response) => response.text())
        .then((data) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data, 'text/html');

          const oldGallery = document.querySelector('.product__media-wrapper');
          const newGallery = doc.querySelector('.product__media-wrapper');

          if (oldGallery && newGallery) {
            replaceGalleryPreservingScroll(oldGallery, newGallery, () => {
              const galleryContainer = document.querySelector('.ProductDetails');

              if (galleryContainer) {
                loadImageGalleries(galleryContainer);
              }
            });
          }
        });

      if (input.value == 0 && input.value < max) {
        const value = parseInt(input.value);
        input.value = value + 1;
        this.totalSelected++;

        this.checkAddToCartConditions();

        selectedQuantity.textContent = input.value;
        if (!selectedQuantity.classList.contains('has-quantity')) {
          selectedQuantity.classList.add('has-quantity');
        }

        const colorValue = container.dataset.colorName;

        if (!this.colorsSelected.includes(this.capitalizeFirstLetter(colorValue))) {
          this.colorsSelected.push(this.capitalizeFirstLetter(colorValue));
        }
        this.adjustLabel();
      }

      if (selected) {
        selected.classList.remove('selected');
      }

      container.classList.add('selected');

      const parentModel = container.dataset.parentModel;

      //   window.dataLayer.push({
      //     event: 'pdp_checkbox_swatch_click',
      //     parent_model: parentModel,
      //     color: container.dataset.colorName,
      //     action: 'click',
      //   });
    }

    increment(e) {
      e.preventDefault();
      if (!comboRowActive(e.target)) return;
      holdViewportAnchor(this, 400);
      const selectedQuantity = e.target.closest('.bundle-swatch-container').querySelector('.bundle-selected-quantity');
      const input = e.target.parentElement.querySelector('input');
      const max = parseInt(input.getAttribute('max'));
      const value = parseInt(input.value);
      if (value < max) {
        input.value = value + 1;
        this.totalSelected++;

        this.checkAddToCartConditions();
      }

      selectedQuantity.textContent = input.value;
      if (!selectedQuantity.classList.contains('has-quantity')) {
        selectedQuantity.classList.add('has-quantity');
      }

      const addedColor = e.target.closest('.bundle-swatch-container').querySelector('.bundle-swatch');
      const colorValue = addedColor.dataset.colorName;

      if (!this.colorsSelected.includes(this.capitalizeFirstLetter(colorValue))) {
        this.colorsSelected.push(this.capitalizeFirstLetter(colorValue));
      }
      this.adjustLabel();
    }

    decrement(e) {
      e.preventDefault();
      if (!comboRowActive(e.target)) return;
      holdViewportAnchor(this, 400);
      const selectedQuantity = e.target.closest('.bundle-swatch-container').querySelector('.bundle-selected-quantity');
      const input = e.target.parentElement.querySelector('input');
      const value = parseInt(input.value);
      if (value > 0) {
        input.value = value - 1;
        this.totalSelected--;

        this.checkAddToCartConditions();
      }

      if (input.value > 0) {
        selectedQuantity.textContent = input.value;
        selectedQuantity.classList.add('has-quantity');
      } else {
        selectedQuantity.textContent = '';
        selectedQuantity.classList.remove('has-quantity');
      }

      const removedColor = e.target.closest('.bundle-swatch-container').querySelector('.bundle-swatch');
      const colorValue = removedColor.dataset.colorName;

      if (this.colorsSelected.includes(this.capitalizeFirstLetter(colorValue)) && input.value == 0) {
        // combo grids repeat color names across style groups (Smooth/Lace/… can
        // each have a Beige) — drop the name from the label only when NO active
        // row with the same color still has quantity selected
        const colorStillSelected = Array.from(this.inputs).some(
          (inp) =>
            Number(inp.value) > 0 &&
            comboRowActive(inp) &&
            inp.closest('.bundle-swatch-container')?.querySelector('.bundle-swatch')?.dataset.colorName === colorValue,
        );
        if (!colorStillSelected) {
          const index = this.colorsSelected.indexOf(this.capitalizeFirstLetter(colorValue));
          this.colorsSelected.splice(index, 1);
        }
      }
      this.adjustLabel();
    }

    adjustLabel() {
      if (this.colorsSelected.length == 0) {
        this.customSwatchesLabel.innerHTML = 'Color';
      } else {
        this.customSwatchesLabel.innerHTML = `Colors: <span class="selected-colors">${this.colorsSelected.join(
          ', ',
        )}</span>`;
      }
    }

    collapseSection(element) {
      const sectionHeight = element.scrollHeight;
      const elementTransition = element.style.transition;

      element.style.transition = '';

      requestAnimationFrame(function () {
        element.style.height = sectionHeight + 'px';
        element.style.transition = elementTransition;

        requestAnimationFrame(function () {
          element.style.height = 0 + 'px';
        });
      });

      element.setAttribute('data-collapsed', 'true');
    }

    expandSection(element) {
      const sectionHeight = element.scrollHeight;

      const handleTransitionEnd = (e) => {
        element.style.height = 'auto';
        element.removeEventListener('transitionend', handleTransitionEnd);
      };

      element.style.height = sectionHeight + 'px';
      element.addEventListener('transitionend', handleTransitionEnd);
      element.setAttribute('data-collapsed', 'false');
    }

    syncArmState() {
      // Idempotent: bring pickers + OOS-dependent UI in line with the stamped arm
      // (html[data-combo-arm]). Called from initialize() when the arm was stamped
      // before this element connected (the common fast path — see spec §3.2
      // state-vs-event contract), and from the module-scope combo:arm-revealed
      // listener for late stamps (3s fallback / slow Intelligems).
      const checkboxBox = this.checkbox?.querySelector('.custom-purchase-option-checkbox');
      const isChecked = !!(checkboxBox && checkboxBox.classList.contains('custom-checkbox-check'));

      document.querySelectorAll('product-swatches').forEach((el) => {
        if (el.offsetParent === null) return; // still hidden (losing arm)
        // Clear any stuck inline height from a pre-reveal toggle cycle, then
        // apply the CURRENT checkbox state without animation.
        el.style.transition = 'none';
        el.style.height = '';
        if (isChecked) {
          el.style.height = '0px';
          el.setAttribute('data-collapsed', 'true');
        } else {
          el.removeAttribute('data-collapsed');
        }
        void el.offsetHeight; // commit the height while transitions are off (rAF fires pre-style-pass and would let all three writes coalesce)
        el.style.transition = '';
      });

      // Re-run the OOS truth over cohort-active rows (a combined reveal can turn
      // an own-style-only allOOS=true into false via sibling stock, and vice versa)
      this.allOOS = Array.from(this.swatches)
        .filter((swatch) => comboRowActive(swatch))
        .every((swatch) => swatch.classList.contains('bundle-swatch-oos'));
      if (this.allOOS) {
        this.atcBtn.style.display = 'none';
        this.customSwatchesLabel.style.display = 'none';
      } else {
        this.customSwatchesLabel.style.display = '';
        if (this.atcBtn.style.display === 'none') this.atcBtn.style.display = '';
        if (isChecked && window.innerWidth >= 990) this.atcBtn.style.display = 'block';
      }
      if (isChecked) {
        const bisContainer = document.querySelector('.klaviyo-bis-container');
        if (bisContainer) bisContainer.style.display = this.allOOS ? 'block' : 'none';
      }
      this.checkAddToCartConditions();
    }
  }

  customElements.define('custom-swatches', CustomSwatches);
}
